/**
 * Remembers which Healing Journey a member was in when a journey step sent
 * them to a tool (Breathing, Grounding, EFT…), so every "Back" on that tool
 * returns to the same journey page and scroll position instead of Home.
 */
import { useEffect, useState } from "react";

const KEY = "rt:journey-return:v1";
const MAX_AGE_MS = 3 * 60 * 60 * 1000;

const JOURNEYS: { prefix: string; label: string }[] = [
  { prefix: "/breaking-free", label: "Breaking Free" },
  { prefix: "/secure-within", label: "Secure Within" },
  { prefix: "/nervous-system-reset", label: "Reset & Regulate" },
  { prefix: "/shadow-journey", label: "Shadow Journey" },
  { prefix: "/my-healing-journey", label: "My Healing Journey" },
  { prefix: "/healing-journeys", label: "Healing Journeys" },
];

export type JourneyReturn = {
  path: string; // pathname + search of the journey page
  label: string;
  scrollY: number;
  toolPath: string; // the page the member tapped through to
  at: number;
};

export function journeyFor(pathname: string) {
  return JOURNEYS.find((j) => pathname === j.prefix || pathname.startsWith(j.prefix + "/")) ?? null;
}

export function readJourneyReturn(): JourneyReturn | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as JourneyReturn;
    if (Date.now() - v.at > MAX_AGE_MS) return null;
    return v;
  } catch {
    return null;
  }
}

export function clearJourneyReturn() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  window.dispatchEvent(new Event("rt:journey-return"));
}

/**
 * Installed once from the root layout. Tracks the reading position on journey
 * pages so it is known whenever the member leaves — link, button or any
 * programmatic navigation.
 */
let lastJourney: { path: string; scrollY: number } | null = null;
export function installJourneyReturnTracking(): () => void {
  const onScroll = () => {
    if (journeyFor(window.location.pathname)) {
      lastJourney = { path: window.location.pathname + window.location.search, scrollY: window.scrollY };
    }
  };
  // Plain <a href> links reload the whole page, so the route-change hook never
  // sees where the member came from. Record on the click itself as well.
  const onClick = (e: MouseEvent) => {
    const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || a.target === "_blank") return;
    const j = journeyFor(window.location.pathname);
    if (!j) return;
    let url: URL;
    try { url = new URL(a.href, window.location.href); } catch { return; }
    if (url.origin !== window.location.origin || journeyFor(url.pathname) || url.pathname === "/") return;
    const v: JourneyReturn = {
      path: window.location.pathname + window.location.search,
      label: j.label,
      scrollY: window.scrollY,
      toolPath: url.pathname,
      at: Date.now(),
    };
    try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch { /* ignore */ }
  };
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("click", onClick, true);
  return () => {
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("click", onClick, true);
  };
}

/** Call when the pathname changes: landing on a journey or Home ends the detour. */
export function onRouteChangeForJourney(pathname: string, prevPathname: string | null) {
  // Leaving a journey for any other page (except Home): remember where from.
  if (prevPathname && journeyFor(prevPathname) && !journeyFor(pathname) && pathname !== "/") {
    const j = journeyFor(prevPathname)!;
    const snap = lastJourney && lastJourney.path.split("?")[0] === prevPathname
      ? lastJourney
      : { path: prevPathname, scrollY: 0 };
    const v: JourneyReturn = { path: snap.path, label: j.label, scrollY: snap.scrollY, toolPath: pathname, at: Date.now() };
    try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch { /* ignore */ }
    window.dispatchEvent(new Event("rt:journey-return"));
    return;
  }
  {
    const v = readJourneyReturn();
    if (v && !journeyFor(pathname) && pathname !== "/" && v.path.split("?")[0] === pathname) {
      clearJourneyReturn(); // back at a non-journey origin, e.g. a journal reflection
      return;
    }
  }
  if (pathname === "/" || journeyFor(pathname)) {
    const v = readJourneyReturn();
    if (!v) return;
    // Back on the journey page (our button OR the browser/app back): put the
    // member where they were. Retry while the page's content loads in.
    if (v.path.split("?")[0] === pathname && v.scrollY > 0) restoreScroll(v.scrollY);
    clearJourneyReturn();
  }
}

function restoreScroll(y: number) {
  const w = window as Window & { __rtRestoringScroll?: boolean };
  w.__rtRestoringScroll = true;
  window.setTimeout(() => { w.__rtRestoringScroll = false; }, 1500);
  let tries = 0;
  const tick = () => {
    tries += 1;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max >= y - 4) {
      window.scrollTo({ top: y, behavior: "instant" as ScrollBehavior });
      if ((Math.abs(window.scrollY - y) < 8 && tries > 8) || tries > 40) return;
    }
    if (tries <= 40) window.setTimeout(tick, 100);
  };
  window.setTimeout(tick, 60);
}

export function useJourneyReturn(): JourneyReturn | null {
  const [v, setV] = useState<JourneyReturn | null>(null);
  useEffect(() => {
    const sync = () => setV(readJourneyReturn());
    sync();
    window.addEventListener("rt:journey-return", sync);
    return () => window.removeEventListener("rt:journey-return", sync);
  }, []);
  return v;
}

/**
 * Go back to the journey. If the tool page was opened straight from the
 * journey, use real history so the journey keeps its scroll and state;
 * otherwise navigate there and restore the saved scroll position.
 */
export function goBackToJourney(
  v: JourneyReturn,
  navigate: (href: string) => void,
) {
  const direct = window.location.pathname === v.toolPath && window.history.length > 1;
  if (direct) {
    window.history.back();
    return;
  }
  navigate(v.path);
}

/** Remember an explicit return point (e.g. a journal reflection) before opening a tool. */
export function rememberReturn(path: string, label: string, toolPath: string) {
  const v: JourneyReturn = { path, label, scrollY: 0, toolPath, at: Date.now() };
  try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch { /* ignore */ }
  window.dispatchEvent(new Event("rt:journey-return"));
}
